import { useFormContext } from 'react-hook-form';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from 'react-query';
import ImageInput from '../../../../../../shared/ImageInput';
import { TextareaComponent } from '../../../../../../shared/TextareaComponent';
import { ServicesTable } from '../../../components/ServicesTable';
import styles from '../../../CreateOrderServiceForm/create-order-service-form.module.css';
import { OrderServiceFormDTO } from '../../../CreateOrderServiceForm/OrderServiceDTO';
import { currencyMask } from '../../../../../../services/helpers/mask';

import { LoadingSpinner } from '../../../../../../shared/LoadingSpinner';
import { OrderServiceSuccessModal } from '../../../components/OrderServiceSuccessModal';
import { updateRepairController } from '../../../controller';
import { parseNumber, parseOSFormChanges } from '../../parseOrderServiceForm';
import { getChangedFields } from '../../getChangedFields';
import { CreateRepairInput } from '../../../../../../@types/order-service';

interface Props {
  originalRepairRef: RefObject<OrderServiceFormDTO | null>;
}

export const ServicesForm = ({ originalRepairRef }: Props) => {
  const { register, watch, setValue, getValues } =
    useFormContext<OrderServiceFormDTO>();
  const queryClient = useQueryClient();

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const serviceImages = watch('services.images') || [];
  const discount = watch('services.discount') || '0';

  const [loadingStatus, setLoadingStatus] = useState<
    'in_progress' | 'finished' | null
  >(null);

  const [serviceTotal, setServiceTotal] = useState(0);
  const [productTotal, setProductTotal] = useState(0);

  const total = useMemo(
    () => serviceTotal + productTotal - parseNumber(discount, true),
    [discount, productTotal, serviceTotal],
  );

  const handleImageChange = useCallback(
    (images: string[]) => {
      setValue(
        'services.images',
        images.map(img => ({ category: 'services', img_url: img })),
      );
    },
    [setValue],
  );

  const serviceFormImages = useMemo(() => {
    return serviceImages.map(img => img.img_url);
  }, [serviceImages]);

  const totalStyle = {
    color: total >= 0 ? '#000' : 'var(--error)',
  };

  const isFormValid = total > 0;

  const handleTransformDraft = async (status: 'finished' | 'in_progress') => {
    setLoadingStatus(status);
    try {
      const current = getValues();
      const original = originalRepairRef.current;

      if (!original) throw new Error('Dados originais não carregados');

      const changedFields = getChangedFields(current, original);

      const updateObject: Partial<CreateRepairInput> = {
        no_client: original.customer.noClient,
        no_vehicle: original.vehicle.noVehicle,
        ...parseOSFormChanges(changedFields, status),
      };

      // atualiza a OS
      await updateRepairController(updateObject);

      // força atualização da lista de OS
      queryClient.invalidateQueries(['repairs']);
      setValue('isDraft', false);
      setShowSuccessModal(true);
    } catch (err) {
      setShowSuccessModal(false);
      setValue('isDraft', true);
    } finally {
      setLoadingStatus(null);
    }
  };

  return (
    <form className={`${styles.form} `}>
      {showSuccessModal && <OrderServiceSuccessModal isEditing />}
      <div className={styles['form-row']}>
        <TextareaComponent
          {...register('services.comment')}
          label="Observações: "
          hint="Informações adicionais sobre a sua O.S."
          className={styles.textarea}
        />
      </div>
      <div className={styles['form-row']}>
        <div className={styles['image-input-wrapper']}>
          <span>Enviar Imagem:</span>
          <ImageInput
            size={68}
            className={styles['image-input']}
            imageClassName={styles.image}
            imagesUrls={serviceFormImages}
            setImageUrls={handleImageChange}
            bucketPath="checklist"
          />
        </div>
      </div>
      <div className={`${styles['form-column']} ${styles['border-bottom']}`}>
        <div className={styles['services-table-wrapper']}>
          <ServicesTable
            {...register('services.productTable')}
            type="product"
            setTotal={setProductTotal}
          />
        </div>
        <div className={styles['services-table-wrapper']}>
          <ServicesTable
            {...register('services.serviceTable')}
            type="service"
            setTotal={setServiceTotal}
          />
        </div>
      </div>
      <div className={`${styles['form-row']} ${styles.info}`}>
        <span>Subtotal de produtos</span>
        <span>
          {productTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      </div>
      <div className={`${styles['form-row']} ${styles.info}`}>
        <span>Subtotal de serviços</span>
        <span>
          {serviceTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      </div>
      <div className={`${styles['form-row']} ${styles.info}`}>
        <span>Desconto</span>
        <div className={styles['discount-input']}>
          <input
            {...register('services.discount')}
            onChange={e => {
              const newValue = currencyMask(e.target.value).replace('R$', '');
              setValue('services.discount', newValue);
            }}
            type="text"
            placeholder="R$ 00,00"
          />
        </div>
      </div>
      <div className={`${styles['form-row']} ${styles.info} ${styles.total}`}>
        <span>TOTAL</span>
        <span style={totalStyle}>
          {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      </div>
      <div className={styles['save-form-container']}>
        <span>Status: </span>
        <div className={styles['save-form--row']}>
          <button
            type="button"
            className={styles['primary-btn']}
            disabled={!isFormValid}
            onClick={async () => {
              await handleTransformDraft('in_progress');
            }}
          >
            Em andamento
            {loadingStatus === 'in_progress' && <LoadingSpinner />}
          </button>
          <button
            type="button"
            className={styles['second-btn']}
            disabled={!isFormValid}
            onClick={async () => {
              await handleTransformDraft('finished');
            }}
          >
            Finalizado
            {loadingStatus === 'finished' && <LoadingSpinner />}
          </button>
        </div>
      </div>
    </form>
  );
};
